/**
 * Metrics Service
 *
 * Pure business logic for deep work metrics calculations.
 * No React or Firebase dependencies.
 */

import { Block, DailyMetrics, WeeklyMetrics } from '@/types';
import { COLORS } from '@/constants/colors';

// ============================================
// Constants
// ============================================

const DEEP_WORK_COLOR = COLORS.deepWork; // #2b6cb0
const SHALLOW_WORK_COLOR = COLORS.shallow; // #9f7aea

// ============================================
// Week Parsing
// ============================================

/**
 * Parse a YYYY-Www week ID into start and end dates.
 *
 * @param weekId - Week ID in "YYYY-Www" format (e.g., "2024-W03")
 * @returns Object with weekStart and weekEnd Date objects
 * @throws Error if weekId format is invalid
 */
export function parseWeekId(weekId: string): { weekStart: Date; weekEnd: Date } {
  const match = weekId.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid weekId format: ${weekId}. Expected YYYY-Www`);
  }

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);

  // Calculate the date of the Monday for ISO week
  // January 4th is always in week 1
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setDate(jan4.getDate() - dayOfWeek + 1);

  // Calculate the Monday of the requested week
  const weekStart = new Date(mondayOfWeek1);
  weekStart.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);

  // Sunday is 6 days after Monday
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return { weekStart, weekEnd };
}

// ============================================
// Date Formatting
// ============================================

/**
 * Format a date as YYYY-MM-DD.
 *
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get all date keys for a week starting from the given Monday.
 *
 * @param weekStart - The Monday of the week
 * @returns Array of 7 date strings in YYYY-MM-DD format
 */
export function getWeekDateKeys(weekStart: Date): string[] {
  const dateKeys: string[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    dateKeys.push(formatDateKey(date));
  }
  return dateKeys;
}

// ============================================
// Daily Metrics Calculation
// ============================================

/**
 * Calculate daily metrics from blocks data.
 *
 * @param date - The date string (YYYY-MM-DD)
 * @param blocks - Array of blocks for the day
 * @param revisionCount - Number of revisions made that day
 * @param mitsCompleted - Number of MITs completed
 * @param mitsTotal - Total number of MITs
 * @returns Calculated daily metrics
 */
export function calculateDailyMetricsFromBlocks(
  date: string,
  blocks: Block[],
  revisionCount: number,
  mitsCompleted: number,
  mitsTotal: number
): DailyMetrics {
  let deepWorkMinutes = 0;
  let shallowWorkMinutes = 0;
  let totalBlockedMinutes = 0;
  let completedBlockCount = 0;
  let plannedBlockCount = 0;
  let missedBlockCount = 0;
  let missedMinutes = 0;

  for (const block of blocks) {
    // Skip cancelled blocks for metrics
    if (block.status === 'cancelled') {
      continue;
    }

    totalBlockedMinutes += block.duration;

    // Count by status
    if (block.status === 'completed') {
      completedBlockCount++;
    } else if (block.status === 'planned') {
      plannedBlockCount++;
    } else if (block.status === 'missed') {
      missedBlockCount++;
      missedMinutes += block.duration;
    }

    // Categorize by color (Deep Work vs Shallow Work)
    if (block.color === DEEP_WORK_COLOR) {
      deepWorkMinutes += block.duration;
    } else if (block.color === SHALLOW_WORK_COLOR) {
      shallowWorkMinutes += block.duration;
    }
  }

  return {
    date,
    deepWorkMinutes,
    shallowWorkMinutes,
    totalBlockedMinutes,
    completedBlockCount,
    plannedBlockCount,
    missedBlockCount,
    missedMinutes,
    revisionCount,
    mitsCompleted,
    mitsTotal,
  };
}

/**
 * Create empty metrics for a date.
 *
 * @param date - The date string (YYYY-MM-DD)
 * @returns Empty daily metrics object
 */
export function createEmptyDailyMetrics(date: string): DailyMetrics {
  return {
    date,
    deepWorkMinutes: 0,
    shallowWorkMinutes: 0,
    totalBlockedMinutes: 0,
    completedBlockCount: 0,
    plannedBlockCount: 0,
    missedBlockCount: 0,
    missedMinutes: 0,
    revisionCount: 0,
    mitsCompleted: 0,
    mitsTotal: 0,
  };
}

// ============================================
// Weekly Metrics Calculation
// ============================================

/**
 * Calculate weekly metrics from daily breakdown.
 *
 * @param weekId - The week ID (YYYY-Www)
 * @param dailyBreakdown - Array of daily metrics
 * @param weekStart - Start date of the week
 * @param weekEnd - End date of the week
 * @returns Calculated weekly metrics
 */
export function calculateWeeklyMetrics(
  weekId: string,
  dailyBreakdown: DailyMetrics[],
  weekStart: Date,
  weekEnd: Date
): WeeklyMetrics {
  let totalDeepWorkMinutes = 0;
  let totalShallowWorkMinutes = 0;
  let bestDeepWorkDay: string | null = null;
  let bestDeepWorkMinutes = 0;

  for (const daily of dailyBreakdown) {
    totalDeepWorkMinutes += daily.deepWorkMinutes;
    totalShallowWorkMinutes += daily.shallowWorkMinutes;

    if (daily.deepWorkMinutes > bestDeepWorkMinutes) {
      bestDeepWorkMinutes = daily.deepWorkMinutes;
      bestDeepWorkDay = daily.date;
    }
  }

  // Calculate average (only for days with any deep work)
  const daysWithDeepWork = dailyBreakdown.filter((d) => d.deepWorkMinutes > 0).length;
  const averageDeepWorkPerDay =
    daysWithDeepWork > 0 ? Math.round(totalDeepWorkMinutes / daysWithDeepWork) : 0;

  return {
    weekId,
    weekStart: formatDateKey(weekStart),
    weekEnd: formatDateKey(weekEnd),
    totalDeepWorkMinutes,
    totalShallowWorkMinutes,
    averageDeepWorkPerDay,
    bestDeepWorkDay,
    goalsCompleted: 0, // Populated separately from weekly plan
    goalsTotal: 0,
    dailyBreakdown,
  };
}

// ============================================
// Duration Formatting
// ============================================

/**
 * Format minutes as a human-readable duration string.
 *
 * @param minutes - Duration in minutes
 * @returns Formatted string (e.g., "2h 30m", "45m", "3h")
 */
export function formatDuration(minutes: number): string {
  if (minutes === 0) return '0m';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

// ============================================
// Current Week ID
// ============================================

/**
 * Get the current week ID.
 *
 * @returns Current week ID in YYYY-Www format
 */
export function getCurrentWeekId(): string {
  const now = new Date();
  const year = now.getFullYear();

  // Get the ISO week number
  const jan4 = new Date(year, 0, 4);
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(year, 0, 1).getTime()) / (24 * 60 * 60 * 1000)
  );
  const dayOfJan4 = Math.floor(
    (jan4.getTime() - new Date(year, 0, 1).getTime()) / (24 * 60 * 60 * 1000)
  );
  const weekNum = Math.ceil((dayOfYear - dayOfJan4 + jan4.getDay() + 1) / 7);

  return `${year}-W${String(weekNum).padStart(2, '0')}`;
}
